export type SalesDeskInventoryOrderInput = {
  id: string;
  supplier: string;
  style: string;
  size: string;
  siding: string;
  sidingColor: string;
  roof: string;
  floor: string;
  price: number | null;
};

export type SalesDeskInventoryPiece = SalesDeskInventoryOrderInput & {
  label: string;
};

export type SalesDeskInventoryModel = {
  key: string;
  supplier: string;
  style: string;
  size: string;
  label: string;
  /** Filled when the organization adopts this configuration as standard stock. */
  standard: null;
  pieces: SalesDeskInventoryPiece[];
};

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function bareMeasure(value: string): string {
  return value.replace(/['"′″]/g, "").replace(/\s+/g, "");
}

export function salesDeskInventorySize(width: string, length: string, size: string): string {
  const explicit = bareMeasure(size);
  if (explicit) return explicit.toLowerCase();
  const pair = [bareMeasure(width), bareMeasure(length)].filter(Boolean);
  return pair.length === 2 ? pair.join("x").toLowerCase() : "";
}

export function salesDeskInventoryOrderFromOptions(
  id: string,
  options: Record<string, unknown>,
): SalesDeskInventoryOrderInput | null {
  const style = text(options.style);
  const size = salesDeskInventorySize(text(options.width), text(options.length), text(options.size));
  if (!style || !size) return null;
  const lines = Array.isArray(options.line_items) ? options.line_items : [];
  let siding = "";
  let floor = "";
  for (const line of lines) {
    if (!line || typeof line !== "object") continue;
    const row = line as Record<string, unknown>;
    const description = text(row.description);
    const productKey = text(row.product_key);
    if (!siding && (productKey === "siding" || description.toLowerCase().startsWith("siding:"))) {
      siding = description.replace(/^siding:\s*/i, "");
    }
    if (!floor && description.toLowerCase().includes("floor joist")) floor = description;
  }
  const roofColor = text(options.roof_color);
  const roofMaterial = text(options.roof_material);
  const price = typeof options.total_price === "number" ? options.total_price : null;
  return {
    id,
    supplier: text(options.supplier) || "IdeaRoom",
    style,
    size,
    siding,
    sidingColor: text(options.siding_color),
    roof: [roofColor, roofMaterial].filter(Boolean).join(" "),
    floor,
    price,
  };
}

export function salesDeskInventoryModels(orders: SalesDeskInventoryOrderInput[]): SalesDeskInventoryModel[] {
  const groups = new Map<string, SalesDeskInventoryModel>();
  for (const order of orders) {
    const supplier = order.supplier.trim() || "IdeaRoom";
    const style = order.style.trim();
    const size = order.size.trim().toLowerCase();
    if (!style || !size) continue;
    const key = `${supplier.toLowerCase()}|${style.toLowerCase()}|${size}`;
    const piece: SalesDeskInventoryPiece = { ...order, supplier, style, size, label: `${size} ${style}` };
    const existing = groups.get(key);
    if (existing) {
      existing.pieces.push(piece);
      continue;
    }
    groups.set(key, {
      key,
      supplier,
      style,
      size,
      label: `${size} ${style}`,
      standard: null,
      pieces: [piece],
    });
  }
  return [...groups.values()];
}

export function filterSalesDeskInventoryModels(
  models: SalesDeskInventoryModel[],
  query: string,
): SalesDeskInventoryModel[] {
  const q = query.trim().toLowerCase();
  if (!q) return models;
  return models
    .map((model) => {
      const modelHit = [model.label, model.supplier, model.style, model.size].join(" ").toLowerCase().includes(q);
      if (modelHit) return model;
      const pieces = model.pieces.filter((piece) =>
        [piece.label, piece.id, piece.siding, piece.sidingColor, piece.roof, piece.floor].join(" ").toLowerCase().includes(q),
      );
      return pieces.length ? { ...model, pieces } : null;
    })
    .filter((model): model is SalesDeskInventoryModel => Boolean(model));
}
